import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Filter,
  Eye,
  Download,
  Trash2,
  LogIn,
  LogOut,
  UserPlus,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Shield,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuditLogs } from '@/hooks/useAudit'
import { useRequireAdmin } from '@/hooks/useAuth'
import { formatDateTime } from '@/lib/utils'

const ITEMS_PER_PAGE = 50

const ACTION_ICONS: Record<string, typeof Eye> = {
  view: Eye,
  download: Download,
  delete: Trash2,
  login: LogIn,
  logout: LogOut,
  login_failed: LogIn,
  create: UserPlus,
  update: Pencil,
}

const ACTION_LABELS: Record<string, string> = {
  view: 'Visualização',
  download: 'Download',
  delete: 'Exclusão',
  login: 'Login',
  logout: 'Logout',
  login_failed: 'Login Falhou',
  create: 'Criação',
  update: 'Atualização',
}

const ACTION_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info'> = {
  view: 'secondary',
  download: 'info',
  delete: 'destructive',
  login: 'success',
  logout: 'secondary',
  login_failed: 'warning',
  create: 'success',
  update: 'info',
}

export function AuditPage() {
  const { isAdmin, isLoading: authLoading } = useRequireAdmin()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get filter values from URL
  const action = searchParams.get('action') || ''
  const resourceType = searchParams.get('resource_type') || ''
  const startDate = searchParams.get('start_date') || ''
  const endDate = searchParams.get('end_date') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)

  const offset = (page - 1) * ITEMS_PER_PAGE

  const { data, isLoading, error } = useAuditLogs({
    action: action || undefined,
    resource_type: resourceType || undefined,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    limit: ITEMS_PER_PAGE,
    offset,
  })

  const totalPages = useMemo(() => {
    if (!data?.total) return 1
    return Math.ceil(data.total / ITEMS_PER_PAGE)
  }, [data?.total])

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', newPage.toString())
    setSearchParams(params)
  }

  const clearFilters = () => {
    setSearchParams({})
  }

  const hasFilters = action || resourceType || startDate || endDate

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Acesso restrito a administradores
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Auditoria
        </h1>
        <p className="text-muted-foreground">
          Logs de acesso e ações do sistema (LGPD)
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <select
          value={action}
          onChange={(e) => updateFilter('action', e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="">Todas as ações</option>
          <option value="view">Visualização</option>
          <option value="download">Download</option>
          <option value="delete">Exclusão</option>
          <option value="create">Criação</option>
          <option value="update">Atualização</option>
          <option value="login">Login</option>
          <option value="logout">Logout</option>
          <option value="login_failed">Login Falhou</option>
        </select>

        <select
          value={resourceType}
          onChange={(e) => updateFilter('resource_type', e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="">Todos os recursos</option>
          <option value="transcript">Transcrição</option>
          <option value="user">Usuário</option>
          <option value="audio">Áudio</option>
        </select>

        <Input
          type="date"
          value={startDate}
          onChange={(e) => updateFilter('start_date', e.target.value)}
          className="w-36"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => updateFilter('end_date', e.target.value)}
          className="w-36"
        />

        {hasFilters && (
          <Button variant="ghost" size="icon" onClick={clearFilters}>
            <Filter className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Results count */}
      {data && (
        <div className="text-sm text-muted-foreground">
          {data.total === 0
            ? 'Nenhum registro encontrado'
            : `Mostrando ${offset + 1}-${Math.min(offset + ITEMS_PER_PAGE, data.total)} de ${data.total} registros`}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64 text-destructive">
          Erro ao carregar logs de auditoria
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data/Hora</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Ação</TableHead>
                <TableHead>Recurso</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Detalhes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data?.data || data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Shield className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">Nenhum registro encontrado</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.data.map((log) => {
                  const ActionIcon = ACTION_ICONS[log.action] || Eye

                  return (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">
                        {formatDateTime(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[150px] truncate">
                          {log.user_email || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={ACTION_VARIANTS[log.action] || 'secondary'}>
                          <ActionIcon className="h-3 w-3 mr-1" />
                          {ACTION_LABELS[log.action] || log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="text-xs text-muted-foreground">
                            {log.resource_type}
                          </span>
                          {log.resource_identifier && (
                            <p className="text-sm truncate max-w-[150px]">
                              {log.resource_identifier}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {log.ip_address || '-'}
                      </TableCell>
                      <TableCell>
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <pre className="text-xs text-muted-foreground max-w-[200px] truncate">
                            {JSON.stringify(log.details)}
                          </pre>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
          >
            Próxima
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  )
}
